import { useCallback, useEffect, useRef, useState } from 'react';
import { Power } from 'lucide-react';
import { powerOff } from '@/lib/system-api';

interface PowerOffOverlayProps {
  onCancel: () => void;
}

type Phase = 'slide' | 'countdown' | 'shutting_down' | 'error';

const KNOB_SIZE = 64;
const TRACK_HEIGHT = 76;
const TRACK_WIDTH = 360;
const TRACK_PADDING = (TRACK_HEIGHT - KNOB_SIZE) / 2;
const MAX_OFFSET = TRACK_WIDTH - KNOB_SIZE - TRACK_PADDING * 2;
const COUNTDOWN_SECONDS = 5;

export function PowerOffOverlay({ onCancel }: PowerOffOverlayProps) {
  const [phase, setPhase] = useState<Phase>('slide');
  const [offset, setOffset] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);

  const beginCountdown = useCallback(() => {
    setPhase('countdown');
    setSecondsLeft(COUNTDOWN_SECONDS);
  }, []);

  // Countdown ticker → API call
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (secondsLeft <= 0) {
      setPhase('shutting_down');
      powerOff().catch((e: Error) => {
        setErrorMsg(e.message || 'Power-off request failed');
        setPhase('error');
      });
      return;
    }
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [phase, secondsLeft]);

  // Pointer drag handlers (slide phase only)
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (phase !== 'slide') return;
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startOffsetRef.current = offset;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [phase, offset],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current || phase !== 'slide') return;
      const delta = e.clientX - startXRef.current;
      const next = Math.max(0, Math.min(MAX_OFFSET, startOffsetRef.current + delta));
      setOffset(next);
      if (next >= MAX_OFFSET - 1) {
        draggingRef.current = false;
        beginCountdown();
      }
    },
    [phase, beginCountdown],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* not captured */
      }
      // Spring back if not slid all the way
      if (offset < MAX_OFFSET - 1) setOffset(0);
    },
    [offset],
  );

  // Visual: track text fades as knob covers it
  const slideProgress = MAX_OFFSET > 0 ? offset / MAX_OFFSET : 0;
  const trackTextOpacity = Math.max(0, 1 - slideProgress * 1.6);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
      <div className="flex flex-col items-center gap-10 px-6 py-10 max-w-[520px] w-full">
        {phase === 'slide' && (
          <>
            <div className="flex flex-col items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                <Power size={36} className="text-red-500" />
              </div>
              <p className="text-4xl font-semibold text-white">Power off the robot</p>
              <p className="text-lg text-white/70 text-center max-w-md leading-relaxed">
                Slide to confirm. The robot will shut down in {COUNTDOWN_SECONDS} seconds.
              </p>
            </div>

            <div
              className="relative rounded-full overflow-hidden select-none touch-none"
              style={{
                width: TRACK_WIDTH,
                height: TRACK_HEIGHT,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ opacity: trackTextOpacity, transition: 'opacity 60ms linear' }}
              >
                <span className="text-xl text-white/70 tracking-wide">slide to power off</span>
              </div>

              <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className="absolute top-1/2 rounded-full bg-white shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing"
                style={{
                  width: KNOB_SIZE,
                  height: KNOB_SIZE,
                  left: TRACK_PADDING + offset,
                  transform: 'translateY(-50%)',
                  transition: draggingRef.current ? 'none' : 'left 200ms ease-out',
                  touchAction: 'none',
                }}
              >
                <Power size={26} className="text-red-500" />
              </div>
            </div>

            <button
              onClick={onCancel}
              className="text-lg text-white/70 hover:text-white transition px-8 py-3"
            >
              Cancel
            </button>
          </>
        )}

        {phase === 'countdown' && (
          <>
            <div className="w-24 h-24 rounded-full bg-red-500/20 border-2 border-red-500/60 flex items-center justify-center">
              <Power size={44} className="text-red-500" />
            </div>
            <div className="flex flex-col items-center gap-4">
              <p className="text-4xl font-semibold text-white">Powering off in</p>
              <p className="text-9xl font-bold text-red-500 tabular-nums">{secondsLeft}</p>
            </div>
            <button
              onClick={onCancel}
              className="rounded-full border border-white/30 px-10 py-4 text-lg font-semibold text-white hover:bg-white/10 transition"
            >
              Cancel
            </button>
          </>
        )}

        {phase === 'shutting_down' && (
          <>
            <div className="w-24 h-24 rounded-full bg-red-500/20 border-2 border-red-500/60 flex items-center justify-center animate-pulse">
              <Power size={44} className="text-red-500" />
            </div>
            <div className="flex flex-col items-center gap-3">
              <p className="text-3xl font-semibold text-white">Powering off…</p>
              <p className="text-lg text-white/70 text-center max-w-md leading-relaxed">
                The robot is shutting down. The dashboard will become unreachable shortly.
              </p>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="w-24 h-24 rounded-full bg-orange-500/20 border-2 border-orange-500/60 flex items-center justify-center">
              <Power size={44} className="text-orange-500" />
            </div>
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-3xl font-semibold text-white">Power-off failed</p>
              <p className="text-lg text-white/70 max-w-md leading-relaxed">{errorMsg}</p>
            </div>
            <button
              onClick={onCancel}
              className="rounded-full border border-white/30 px-10 py-4 text-lg font-semibold text-white hover:bg-white/10 transition"
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}
